import { forwardRef, useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, LayoutGrid } from "lucide-react";
import { playPageSound } from "../../lib/pageSound";

const Page = forwardRef(({ src, n }, ref) => (
  <div ref={ref} className="bg-white overflow-hidden" data-testid={`flip-page-${n}`}>
    <img src={src} alt={`${n}`} draggable="false" className="w-full h-full object-cover select-none" />
    {n > 1 && <div className={`pointer-events-none absolute inset-y-0 w-10 ${n % 2 === 0 ? "right-0 bg-gradient-to-l" : "left-0 bg-gradient-to-r"} from-black/20 to-transparent`} />}
  </div>
));

function useBookSize() {
  const calc = () => {
    const w = Math.floor(Math.min((window.innerWidth - 160) / 2, (window.innerHeight - 130) / 0.707));
    return { w, h: Math.floor(w * 0.707) };
  };
  const [s, setS] = useState(calc);
  useEffect(() => {
    const r = () => setS(calc());
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);
  return s;
}

export default function BookView({ catalog, lang, sound, zoomOpen, start = 0, onZoom, onPage }) {
  const book = useRef(null);
  const [i, setI] = useState(start);
  const zoomRef = useRef(zoomOpen);
  zoomRef.current = zoomOpen;
  const [thumbs, setThumbs] = useState(false);
  const { w, h } = useBookSize();
  const pages = catalog.hd || catalog.pages;
  const n = pages.length;
  const api = () => book.current?.pageFlip();
  const clickT = useRef(null);
  const wheelT = useRef(0);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  useEffect(() => {
    const k = (e) => { if (zoomRef.current) return; if (e.key === "ArrowRight") api()?.flipNext(); if (e.key === "ArrowLeft") api()?.flipPrev(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  useEffect(() => onPage(i), [i, onPage]);

  const side = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientX - r.left > r.width / 2 ? 1 : -1;
  };
  const down = useRef([0, 0]);
  const onClick = (e) => {
    if (Math.hypot(e.clientX - down.current[0], e.clientY - down.current[1]) > 6) return;
    const d = side(e);
    clearTimeout(clickT.current);
    clickT.current = setTimeout(() => (d > 0 ? api()?.flipNext() : api()?.flipPrev()), 230);
  };
  const onDouble = (e) => {
    clearTimeout(clickT.current);
    onZoom(side(e) > 0 && i > 0 ? Math.min(i + 1, n - 1) : i);
  };
  const onWheel = (e) => {
    const now = Date.now();
    if (now - wheelT.current < 700 || Math.abs(e.deltaY) < 8) return;
    wheelT.current = now;
    e.deltaY > 0 ? api()?.flipNext() : api()?.flipPrev();
  };
  const label = i === 0 || i === n - 1 ? `${i + 1}` : `${i + 1}–${Math.min(i + 2, n)}`;

  return (
    <>
      <div className="relative flex-1 flex items-center justify-center min-h-0" onWheel={onWheel}>
        <div style={{ transform: `translateX(${i === 0 ? -w / 2 : i >= n - 1 && n % 2 === 0 ? w / 2 : 0}px)` }} className="drop-shadow-[0_40px_60px_rgba(0,0,0,0.6)] cursor-pointer transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]" onPointerDown={(e) => { down.current = [e.clientX, e.clientY]; }} onClick={onClick} onDoubleClick={onDouble} data-testid="book-surface">
          <HTMLFlipBook
            key={w}
            ref={book}
            width={w}
            height={h}
            size="fixed"
            showCover
            usePortrait={false}
            maxShadowOpacity={0.45}
            flippingTime={800}
            startPage={start}
            disableFlipByClick
            onFlip={(e) => setI(e.data)}
            onChangeState={(e) => { if (e.data === "flipping" && soundRef.current) playPageSound(); }}
            className="select-none"
          >
            {pages.map((p, k) => <Page key={p} src={p} n={k + 1} />)}
          </HTMLFlipBook>
        </div>
        <button onClick={() => api()?.flipPrev()} disabled={i === 0} className="absolute left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-20 transition-colors" data-testid="viewer-prev"><ChevronLeft className="w-5 h-5" /></button>
        <button onClick={() => api()?.flipNext()} disabled={i + 2 >= n} className="absolute right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-20 transition-colors" data-testid="viewer-next"><ChevronRight className="w-5 h-5" /></button>
      </div>
      <div className="relative h-16 shrink-0 flex items-center justify-center gap-5 text-[12px] text-white/60">
        <button onClick={() => setThumbs((v) => !v)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 transition-colors ${thumbs ? "bg-white text-[#0b0b0d]" : "bg-white/10 hover:bg-white/20 text-white"}`} data-testid="viewer-thumbs-toggle">
          <LayoutGrid className="w-3.5 h-3.5" /> {lang === "it" ? "Pagine" : "Pages"}
        </button>
        <span data-testid="viewer-counter">{label} / {n}</span>
        <span className="hidden lg:inline text-white/40">{lang === "it" ? "Clic per girare · doppio clic per ingrandire · rotella o frecce" : "Click to turn · double-click to zoom · wheel or arrows"}</span>
        {thumbs && (
          <div className="absolute bottom-16 inset-x-0 px-6 pb-2" data-testid="viewer-thumbs">
            <div className="mx-auto max-w-[1100px] rounded-2xl bg-[#1a1b1f]/95 backdrop-blur-xl ring-1 ring-white/10 p-3 overflow-x-auto" data-lenis-prevent>
              <div className="flex gap-2 w-max mx-auto">
                {(catalog.mini || catalog.pages).map((p, k) => (
                  <button key={p} onClick={() => { api()?.turnToPage(k); setI(api()?.getCurrentPageIndex() ?? k); setThumbs(false); }} data-testid={`viewer-thumb-${k + 1}`} className={`shrink-0 w-20 rounded overflow-hidden ring-2 transition-opacity ${k === i || (k === i + 1 && i > 0) ? "ring-white" : "ring-transparent opacity-50 hover:opacity-100"}`}>
                    <img src={p} alt="" loading="lazy" className="w-full h-auto block" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
